import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Token management
export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

export const getAccessToken = () => localStorage.getItem('accessToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');

export const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

const client = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Queued refresh: only one refresh call at a time, others wait for it
let refreshPromise = null;

async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = response.data;
  setTokens(accessToken, newRefreshToken);
  return accessToken;
}

// Response interceptor for token refresh
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in progress, wait for it
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
          });
        }

        const newAccessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        clearTokens();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  requestLoginOTP: (email) => client.post('/auth/login/request', { email }),
  verifyLoginOTP: (email, code, name, baseCurrency) => client.post('/auth/login/verify', { email, code, name, baseCurrency }),
  getCurrentUser: () => client.get('/auth/me'),
  logout: () => client.post('/auth/logout', {}),
  updateProfile: (data) => client.put('/auth/profile', data),
  getGoogleLoginUrl: () => client.get('/auth/google/login'),
  getGmailConnectUrl: () => client.get('/auth/gmail/connect'),
  disconnectGmail: () => client.post('/auth/gmail/disconnect'),
  getOutlookConnectUrl: () => client.get('/auth/outlook/connect'),
  disconnectOutlook: () => client.post('/auth/outlook/disconnect'),
  deleteAccount: () => client.delete('/auth/account'),
};

export const linkedEmailAPI = {
  getAll: () => client.get('/api/linked-emails'),
  requestOTP: (email) => client.post('/api/linked-emails/request', { email }),
  verify: (email, code) => client.post('/api/linked-emails/verify', { email, code }),
  remove: (email) => client.delete(`/api/linked-emails/${encodeURIComponent(email)}`),
};

export const transactionAPI = {
  sync: (options = {}) => client.post('/api/transactions/sync', options), // options can include { scope, emailSource }

  /**
   * Get computed summary (totals, category breakdown, chart series, top merchants, transactions)
   * for a date range. All aggregation is done server-side.
   * params: { startDate, endDate, groupBy, excludedCategories (comma-sep), categories (comma-sep) }
   */
  getSummary: (params) => client.get('/api/transactions/summary', { params }),
  getBanks: () => client.get('/api/transactions/banks'),

  getAll: (filters) => client.get('/api/transactions', { params: filters }),
  create: (transaction) => client.post('/api/transactions', transaction),
  update: (id, data) => client.put(`/api/transactions/${id}`, data),
  delete: (id) => client.delete(`/api/transactions/${id}`),
  getSyncStatus: () => client.get('/api/transactions/sync-status'),
  getSyncJobStatus: (jobId) => client.get(`/api/transactions/sync-job/${jobId}`),
  getDailyInsights: () => client.get('/api/transactions/daily-insights'),
  getWeeklyInsights: () => client.get('/api/transactions/weekly-insights'),
  getMonthlyInsights: (year, month) => client.get('/api/transactions/monthly-insights', { params: { year, month } }),
  uploadStatement: (formData) => client.post('/api/transactions/upload-statement', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const analyticsAPI = {
  getCategoryTotals: (startDate, endDate) =>
    client.get('/api/analytics/categories', { params: { startDate, endDate } }),
  getMonthlyTrends: (months) =>
    client.get('/api/analytics/trends', { params: { months } }),
  getTopMerchants: (limit) =>
    client.get('/api/analytics/merchants', { params: { limit } }),
};

export const budgetAPI = {
  getAll:   ()         => client.get('/api/budgets'),
  create:   (data)     => client.post('/api/budgets', data),
  update:   (id, data) => client.put(`/api/budgets/${id}`, data),
  remove:   (id)       => client.delete(`/api/budgets/${id}`),
  getSpend: (id)       => client.get(`/api/budgets/${id}/spend`),
};

export const goalsAPI = {
  upsertGoal: (goalData) => client.post('/api/goals', goalData),
  getCurrentGoal: () => client.get('/api/goals/current'),
  getGoal: (year, month) => client.get(`/api/goals/${year}/${month}`),
  getAllGoals: () => client.get('/api/goals/all'),
  deleteGoal: (year, month) => client.delete(`/api/goals/${year}/${month}`),
  getBudgetAdvice: () => client.get('/api/goals/ai/advice'),
  getSuggestedBudgets: () => client.get('/api/goals/ai/suggest-budgets'),
  askQuestion: (question) => client.post('/api/goals/ai/ask', { question }),
  getInsights: () => client.get('/api/goals/ai/insights'),
  getAdviceHistory: (limit) => client.get('/api/goals/ai/history', { params: { limit } }),
};

export default client;
